import React, { ReactNode, Suspense } from 'react';
import { RouterProvider, Navigate, createBrowserRouter, useParams } from 'react-router-dom';
import { useAuth } from '@/lib/hooks/use-auth';
import Login from '@/pages/auth/login';
import Register from '@/pages/auth/register';
import ForgotPassword from '@/pages/auth/forgot-password';
import InviteNewUser from '@/pages/auth/invite-new-user';
import InviteUser from '@/pages/auth/invite-user';
import TokenVerification from '@/pages/auth/token-verification';
import ResetPassword from '@/pages/auth/reset-password';
import SSOStart from '@/pages/auth/sso-start';
import SSOCallback from '@/pages/auth/sso-callback';
import Home from '@/pages/home';
import Analytics from '@/pages/analytics';
import Users from '@/pages/users/list-users';
import UserProfile from '@/pages/users/user-profile';
import EditUser from '@/pages/users/edit-user';
import ListProducts from '@/pages/workspace/list-products';
import Settings from '@/pages/workspace/settings';
import EditAccount from '@/pages/workspace/edit-account';
import SSOSettings from '@/pages/workspace/sso-settings';
import ListFields from '@/pages/fields/list-fields';
import CreateField from '@/pages/fields/create-field';
import EditField from '@/pages/fields/edit-field';
import ListDocuments from '@/pages/documents/list-documents';
import KnowledgeBase from '@/pages/knowledge-base';
import AICreate from '@/pages/ai-create';
import ListContentLibrary from '@/pages/libraries/list-content-library';
import CreateContentLibrary from '@/pages/libraries/create-content-library';
import EditContentLibrary from '@/pages/libraries/edit-content-library';
import Contents from '@/pages/contents/contents';
import CreateContent from '@/pages/contents/create-content';
import EditContent from '@/pages/contents/edit-content';
import PublicContent from '@/pages/contents/public-content';
import PublicContentList from '@/pages/contents/public-content-list';
import CancelSubscription from '@/pages/workspace/cancel-subscription';

import MainLayout from '@/pages/layout/layout';
import AuthLayout from '@/pages/layout/auth';
import PublicLayout from '@/pages/layout/public';
import LimitLayout from '@/pages/layout/limited';
import LimitedHome from '@/pages/limited-home';

import ListWorkspace from '@/pages/workspace/list-workspace';
import ResumeSubscription from '@/pages/workspace/resume-subscription';

import ListCollectionType from './pages/collection-types/list-collection-type';
import CreateCollectionType from './pages/collection-types/create-collection-type';
import EditCollectionType from './pages/collection-types/edit-collection-type';
import Collections from './pages/collections/collections';
import CreateCollection from './pages/collections/create-collection';
import EditCollection from './pages/collections/edit-collection';
import PublicCollection from './pages/collections/public-collection';
import PublicCollectionList from './pages/collections/public-collection-list';
import ListAIContentRequests from './pages/ai-content-requests/list-ai-content-requests';
import EditAIContentRequest from './pages/ai-content-requests/edit-ai-content-request';
import Calendar from './pages/calendar/calendar';
import Planner from './pages/planner/planner';
import ListCourses from './pages/courses/list/list-courses';
import CourseBuilder from './pages/courses/builder/course-builder';
import CourseView from './pages/courses/view/course-view';
import CoursePlayer from './pages/courses/learn/course-player';
import PublicCourse from './pages/courses/public-course';
import ListGroups from './pages/groups/list-groups';
import PublicPlan from './pages/planner/public-plan';
import DeveloperKeys from './pages/developer/keys';
import DeveloperAnalytics from './pages/developer/analytics';
import DevelopersLanding from './pages/developers/landing';
// The docs page renders the full endpoint reference. Code-split so this code
// only ships to visitors of /developers/docs.
const DevelopersDocs = React.lazy(() => import('./pages/developers/docs/index'));

import {
  userProfileLoader,
  usersLoader,
  userLoader,
  productsLoader,
  fieldLoader,
  fieldsLoader,
  documentsLoader,
  knowledgeBaseLoader,
  contentFieldLoader,
  contentLoader,
  sportTypesLoader,
} from '@/utils/loader';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useWorkspace from '@/lib/hooks/use-workspace';
import useFeatureFlag from '@/lib/hooks/use-feature-flag';
interface RouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<RouteProps> = ({ children }) => {
  const isCancelled = useIsCancelled();
  const { workspaceId } = useParams();
  const auth = useAuth();

  if (!auth.isAuthenticated) {
    return <Navigate to='/auth/login' replace />;
  }

  if (isCancelled) {
    return <Navigate to={`/limited/${workspaceId}/home`} replace />;
  }

  return children;
};

const LimitedRoute: React.FC<RouteProps> = ({ children }) => {
  const auth = useAuth();

  if (!auth.isAuthenticated) {
    return <Navigate to='/auth/login' replace />;
  }

  return children;
};

const PublicRoute: React.FC<RouteProps> = ({ children }) => {
  const auth = useAuth();
  const { workspace } = useWorkspace();

  if (auth.isAuthenticated && workspace) {
    return <Navigate to={`/${workspace.id}/home`} replace />;
  }

  return children;
};

// Gates a route behind a query-param feature flag (see useFeatureFlag). When the
// flag is off, redirects away: to the explicit `redirect` if given, otherwise to
// the current workspace's settings (or login when there's no workspace in scope).
const FeatureGate: React.FC<{ flag: string; redirect?: string } & RouteProps> = ({
  flag,
  redirect,
  children,
}) => {
  const enabled = useFeatureFlag(flag);
  const { workspaceId } = useParams();

  if (enabled) return children;

  return <Navigate to={redirect ?? (workspaceId ? `/${workspaceId}/settings` : '/')} replace />;
};

// key on workspaceId forces remount on workspace switch so per-workspace local state cannot leak.
const HomeRoute: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  return <Home key={workspaceId} />;
};

// key on workspaceId forces remount on workspace switch so per-workspace local state cannot leak.
const PlannerRoute: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  return <Planner key={workspaceId} />;
};

// Courses ship dark: the whole feature sits behind the `courses` query-param
// flag (see useFeatureFlag) so it can be previewed on staging and production
// without a deploy or per-environment config — `?flag=courses` turns it on for
// the tab session, `?flag=-courses` turns it off. Flag-less visitors bounce to
// the workspace home rather than FeatureGate's default of Settings, since
// Courses is a member-facing feature, not an admin one.
const CoursesGate: React.FC<RouteProps> = ({ children }) => {
  const { workspaceId } = useParams();
  return (
    <FeatureGate flag='courses' redirect={workspaceId ? `/${workspaceId}/home` : '/'}>
      {children}
    </FeatureGate>
  );
};

// key on workspaceId forces remount on workspace switch so per-workspace local state cannot leak.
const CoursesRoute: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  return <ListCourses key={workspaceId} />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to='/auth/login' replace />, // Redirect to login
  },
  {
    path: '/auth',
    element: (
      <PublicRoute>
        <AuthLayout />
      </PublicRoute>
    ),
    children: [
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'register',
        loader: sportTypesLoader,
        element: <Register />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: 'reset-password/:token',
        element: <ResetPassword />,
      },
      {
        path: 'sso',
        element: (
          <FeatureGate flag='sso' redirect='/auth/login'>
            <SSOStart />
          </FeatureGate>
        ),
      },
      {
        path: 'sso/callback',
        element: (
          <FeatureGate flag='sso' redirect='/auth/login'>
            <SSOCallback />
          </FeatureGate>
        ),
      },
      {
        path: 'verify-email/:token',
        element: <TokenVerification />,
      },
      {
        path: 'invite-new-user/:token',
        element: <InviteNewUser />,
      },
      {
        path: 'invite-user/:token',
        element: <InviteUser />,
      },
    ],
  },
  {
    path: ':workspaceId',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'home',
        element: <HomeRoute />,
      },
      {
        path: 'analytics',
        element: <Analytics />,
      },
      {
        path: 'create',
        element: <AICreate />,
      },
      { path: 'settings', element: <Settings /> },
      { path: 'users', loader: usersLoader, element: <Users /> },
      {
        path: 'users/:userId/edit',
        loader: userLoader,
        element: <EditUser />,
      },
      {
        path: 'my/profile',
        loader: userProfileLoader,
        element: <UserProfile />,
      },
      {
        path: 'billing',
        loader: productsLoader,
        element: <ListProducts />,
      },
      {
        path: 'account/edit',
        element: <EditAccount />,
      },
      {
        path: 'account/sso',
        element: (
          <FeatureGate flag='sso'>
            <SSOSettings />
          </FeatureGate>
        ),
      },
      {
        path: 'fields',
        loader: fieldsLoader,
        element: <ListFields />,
      },
      {
        path: 'fields/create/:fieldType',
        element: <CreateField />,
      },
      {
        path: 'fields/:id/edit',
        loader: fieldLoader,
        element: <EditField />,
      },
      // Knowledge Base per content type (old S3-based)
      {
        path: 'content-types/:contentTypeId/documents',
        loader: documentsLoader,
        element: <ListDocuments />,
      },
      // Gemini Knowledge Base per content type
      {
        path: 'content-types/:contentTypeId/knowledge-base',
        loader: knowledgeBaseLoader,
        element: <KnowledgeBase />,
      },
      // Upload moved to content type actions
      {
        path: 'content-types',
        element: <ListContentLibrary />,
      },
      {
        path: 'content-types/create',
        element: <CreateContentLibrary />,
      },
      {
        path: 'content-types/:id/edit',
        element: <EditContentLibrary />,
      },
      {
        path: 'content-types/:contentTypeId/contents/:tabIndex',
        element: <Contents />,
      },
      {
        path: 'content-types/:contentTypeId/contents/create',
        loader: contentFieldLoader,
        element: <CreateContent />,
      },
      {
        path: 'content-types/:contentTypeId/contents/:id/edit',
        loader: contentFieldLoader,
        element: <EditContent />,
      },
      {
        path: 'content-types/:contentTypeId/ai-content-requests',
        element: <ListAIContentRequests />,
      },
      {
        path: 'content-types/:contentTypeId/ai-content-requests/:id/edit',
        element: <EditAIContentRequest />,
      },
      {
        path: 'workspaces',
        element: <ListWorkspace />,
      },
      {
        path: 'cancel-subscription',
        element: <CancelSubscription />,
      },
      {
        path: 'collection-types',
        element: <ListCollectionType />,
      },
      {
        path: 'collection-types/create',
        element: <CreateCollectionType />,
      },
      {
        path: 'collection-types/:id/edit',
        element: <EditCollectionType />,
      },
      {
        path: 'collection-types/:collectionTypeId/collections/:tabIndex',
        element: <Collections />,
      },
      {
        path: 'collection-types/:collectionTypeId/collections/create',
        element: <CreateCollection />,
      },
      {
        path: 'collection-types/:collectionTypeId/collections/:id/edit',
        element: <EditCollection />,
      },
      {
        path: 'planner',
        element: <PlannerRoute />,
      },
      {
        path: 'courses',
        element: (
          <CoursesGate>
            <CoursesRoute />
          </CoursesGate>
        ),
      },
      {
        path: 'courses/create',
        element: (
          <CoursesGate>
            <CourseBuilder />
          </CoursesGate>
        ),
      },
      {
        path: 'courses/:courseId',
        element: (
          <CoursesGate>
            <CourseView />
          </CoursesGate>
        ),
      },
      {
        path: 'courses/:courseId/build',
        element: (
          <CoursesGate>
            <CourseBuilder />
          </CoursesGate>
        ),
      },
      {
        path: 'courses/:courseId/learn',
        element: (
          <CoursesGate>
            <CoursePlayer />
          </CoursesGate>
        ),
      },
      {
        // Groups are course audiences — gated with Courses.
        path: 'groups',
        element: (
          <CoursesGate>
            <ListGroups />
          </CoursesGate>
        ),
      },
      {
        path: 'calendar/:calendarType',
        element: <Calendar />,
      },
      {
        path: 'developer/keys',
        element: <DeveloperKeys />,
      },
      {
        path: 'developer/analytics',
        element: <DeveloperAnalytics />,
      },
    ],
  },
  {
    path: '/limited',
    element: (
      <LimitedRoute>
        <LimitLayout />
      </LimitedRoute>
    ),
    children: [
      {
        path: ':workspaceId/home',
        element: <LimitedHome />,
      },
      {
        path: ':workspaceId/workspaces',
        element: <ListWorkspace />,
      },
      {
        path: ':workspaceId/resume-subscription',
        element: <ResumeSubscription />,
      },
    ],
  },
  {
    // Public developer portal — accessible to anyone, no auth required.
    // Deliberately NOT wrapped in PublicRoute (which redirects authenticated
    // users away from public pages) — the docs are useful to all visitors.
    path: '/developers',
    children: [
      { index: true, element: <DevelopersLanding /> },
      {
        path: 'docs',
        element: (
          <Suspense fallback={null}>
            <DevelopersDocs />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/public',
    element: (
      <PublicRoute>
        <PublicLayout />
      </PublicRoute>
    ),
    children: [
      {
        path: 'content/:referenceId',
        loader: contentLoader,
        element: <PublicContent />,
      },
      {
        path: 'content-categories/:referenceId',
        element: <PublicContentList />,
      },
      {
        path: 'collections/:referenceId',
        element: <PublicCollection />,
      },
      {
        path: 'collection-types/:referenceId',
        element: <PublicCollectionList />,
      },
    ],
  },
  {
    // Public practice-plan share link (planner Share button). Deliberately
    // NOT wrapped in PublicRoute — recipients who happen to be logged in
    // should see the plan, not get bounced to their workspace home (same
    // reasoning as /developers above).
    path: '/public/planner/:referenceId',
    element: <PublicLayout />,
    children: [{ index: true, element: <PublicPlan /> }],
  },
  {
    // Public course share link. Same reasoning as /public/planner above:
    // outside PublicRoute so signed-in recipients see the course.
    path: '/public/courses/:referenceId',
    element: <PublicLayout />,
    children: [{ index: true, element: <PublicCourse /> }],
  },
]);

const App: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default App;
